/**
 * Public Signal Export V1 - Platform Formatter
 * 
 * Formats final text for different platforms.
 * 
 * BOUNDARIES:
 * - Advisory only: no execution authority
 * - Deterministic output only
 * - No mutation of inputs
 */

import { ExportPlatform } from './types';

/**
 * Format text for LinkedIn
 */
function formatForLinkedIn(text: string, title: string): string {
  return `${title}

${text}`;
}

/**
 * Format text for X (Twitter)
 */
function formatForX(text: string, title: string): string {
  // Truncate if too long for X (280 char limit, but we'll keep it reasonable)
  const maxLength = 280;
  const combined = `${title}\n\n${text}`;
  
  if (combined.length <= maxLength) {
    return combined;
  }
  
  // Truncate with ellipsis
  return combined.substring(0, maxLength - 3) + '...';
}

/**
 * Format text for blog
 */
function formatForBlog(text: string, title: string): string {
  return `# ${title}

${text}`;
}

/**
 * Format text for generic (no specific formatting)
 */
function formatForGeneric(text: string, title: string): string {
  return `${title}\n\n${text}`;
}

/**
 * Format text for platform
 */
export function formatForPlatform(
  text: string,
  title: string,
  platform: ExportPlatform
): string {
  switch (platform) {
    case 'linkedin':
      return formatForLinkedIn(text, title);
    case 'x':
      return formatForX(text, title);
    case 'blog':
      return formatForBlog(text, title);
    case 'generic':
      return formatForGeneric(text, title);
    default:
      return formatForGeneric(text, title);
  }
}

/**
 * Generate copyable text (with platform-specific hints)
 */
export function generateCopyableText(
  formattedText: string,
  platform: ExportPlatform
): string {
  const hints: Record<ExportPlatform, string> = {
    linkedin: 'Copy and paste this to LinkedIn',
    x: 'Copy and paste this to X (Twitter)',
    blog: 'Copy and use this for your blog post',
    generic: 'Copy and use this text',
  };

  return `${formattedText}

---
${hints[platform]}`;
}
